import React, { useState, useEffect } from 'react';
import { X, Save, Eye, Edit3, Plus, Lock, Unlock, Copy, RefreshCw, ExternalLink, Package } from 'lucide-react';
import { Device } from '../lib/supabase';
import { useAuditLog } from '../hooks/useAuditLog';
import { useLockMessages } from '../hooks/useLockMessages';

interface DeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: Device | null;
  mode: 'view' | 'edit' | 'add';
  onSave: (device: Omit<Device, 'id' | 'created_at' | 'updated_at'>) => Promise<{ success: boolean; error?: string }>;
  originalDevice?: Device | null; // Original device data before URL param changes
}

export function DeviceModal({ isOpen, onClose, device, mode, onSave, originalDevice }: DeviceModalProps) {
  const { logAction } = useAuditLog();
  const { messages: lockMessages, loading: lockMessagesLoading } = useLockMessages();
  const [formData, setFormData] = useState({
    serial_number: '',
    device_name: '',
    owner_name: '',
    is_locked: false,
    lock_message_id: null as number | null,
    unlock_code: '',
    note: '',
    with_ict: false,
    details: null as {
      has_charger: boolean;
      has_bag: boolean;
      stack_number?: number;
      position_in_stack?: number;
      recall_date?: string;
      return_due_date?: string;
      condition_notes?: string;
    } | null
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUnlockCode, setShowUnlockCode] = useState(false);

  // Generate a secure unlock code
  const generateUnlockCode = () => {
    // Use a mix of uppercase, lowercase, numbers, and symbols for better security
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%&*';
    const allChars = uppercase + lowercase + numbers + symbols;
    
    let result = '';
    
    // Ensure at least one character from each category
    result += uppercase[Math.floor(Math.random() * uppercase.length)];
    result += lowercase[Math.floor(Math.random() * lowercase.length)];
    result += numbers[Math.floor(Math.random() * numbers.length)];
    result += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the remaining 8 positions with random characters
    for (let i = 4; i < 12; i++) {
      result += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the result to avoid predictable patterns
    return result.split('').sort(() => Math.random() - 0.5).join('');
  };

  useEffect(() => {
    if (device && (mode === 'view' || mode === 'edit')) {
      setFormData({
        serial_number: device.serial_number,
        device_name: device.device_name,
        owner_name: device.owner_name,
        is_locked: device.is_locked,
        lock_message_id: device.lock_message_id ?? null,
        unlock_code: device.unlock_code,
        note: device.note || '',
        with_ict: device.with_ict || false,
        details: device.details || null
      });
    } else if (mode === 'add') {
      setFormData({
        serial_number: device?.serial_number || '',
        device_name: device?.device_name || '',
        owner_name: device?.owner_name || '',
        is_locked: device?.is_locked || false,
        lock_message_id: device?.lock_message_id ?? null,
        unlock_code: device?.unlock_code || generateUnlockCode(),
        note: device?.note || '',
        with_ict: device?.with_ict || false,
        details: device?.details || null
      });
    }
    setError(null);
  }, [device, mode, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') return;

    if (formData.is_locked && !formData.lock_message_id) {
      setError('Please select a lock message template before saving a locked device.');
      return;
    }

    setSaving(true);
    setError(null);

    const result = await onSave(formData);
    if (!result.success) {
      setError(result.error || 'Failed to save device');
    }
    setSaving(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'view';
  const title = mode === 'add' ? 'Add New Device' : mode === 'edit' ? 'Edit Device' : 'Device Details';
  const Icon = mode === 'add' ? Plus : mode === 'edit' ? Edit3 : Eye;
  const selectedLockMessage = lockMessages.find((message) => message.id === formData.lock_message_id);
  const defaultLockMessage = lockMessages.find((message) => message.is_default) || lockMessages[0];

  // Check if this is an edit from URL params (device exists and originalDevice provided)
  const isUrlParamEdit = mode === 'edit' && device && originalDevice;

  // Helper to show field changes
  const FieldComparison = ({ label, currentValue, newValue }: { 
    label: string; 
    currentValue: string | boolean; 
    newValue: string | boolean;
  }) => {
    const hasChanged = currentValue !== newValue;
    if (!hasChanged) return null;

    return (
      <div className="mb-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg">
        <p className="text-sm font-medium text-amber-800 dark:text-amber-400 mb-1">{label} Change:</p>
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-slate-600 dark:text-slate-400">
            From: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{currentValue?.toString() || '(empty)'}</code>
          </span>
          <span className="text-slate-400">→</span>
          <span className="text-emerald-600 dark:text-emerald-400">
            To: <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1 rounded">{newValue?.toString()}</code>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed -top-5 -left-5 -right-5 -bottom-5 bg-black/50 backdrop-blur-md flex items-center justify-center p-4 z-[9999] overflow-hidden" style={{ minHeight: 'calc(100vh + 40px)', minWidth: 'calc(100vw + 40px)', transform: 'translate3d(0, 0, 0)' }}>
      <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden border border-slate-200/60 dark:border-slate-700/60 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200/60 dark:border-slate-700/60 bg-gradient-to-r from-slate-50/80 to-slate-100/80 dark:from-slate-800/80 dark:to-slate-700/80">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
              {device && (
                <p className="text-sm text-slate-600 dark:text-slate-400 font-mono">Serial: {device.serial_number}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4">
                <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* URL Parameter Changes Notice */}
            {isUrlParamEdit && originalDevice && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ExternalLink className="text-white w-3 h-3" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-blue-800 dark:text-blue-400 font-medium mb-2">URL Parameter Changes Detected</h4>
                    <div className="space-y-2">
                      <FieldComparison 
                        label="Device Name" 
                        currentValue={originalDevice.device_name} 
                        newValue={formData.device_name}
                      />
                      <FieldComparison 
                        label="Owner Name" 
                        currentValue={originalDevice.owner_name} 
                        newValue={formData.owner_name}
                      />
                      <FieldComparison 
                        label="Lock Status" 
                        currentValue={originalDevice.is_locked ? 'Locked' : 'Unlocked'} 
                        newValue={formData.is_locked ? 'Locked' : 'Unlocked'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Left Column */}
              <div className="lg:col-span-2 space-y-5">
                {/* Serial Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Serial Number *
                  </label>
                  <input
                    type="text"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    required
                    className={`w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      isReadOnly 
                        ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                        : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                    }`}
                    placeholder="Enter device serial number"
                  />
                </div>

                {/* Device Name and Owner Name Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Device Name
                    </label>
                    <input
                      type="text"
                      name="device_name"
                      value={formData.device_name}
                      onChange={handleChange}
                      readOnly={isReadOnly}
                      className={`w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        isReadOnly 
                          ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                          : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      }`}
                      placeholder="e.g., John's Laptop"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Owner Name
                    </label>
                    <input
                      type="text"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      readOnly={isReadOnly}
                      className={`w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        isReadOnly 
                          ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                          : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      }`}
                      placeholder="Device owner's name"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Notes
                  </label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    rows={2}
                    className={`w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-y ${
                      isReadOnly 
                        ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                        : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                    }`}
                    placeholder="Add any notes about this device..."
                  />
                </div>

                {/* ICT Management Section */}
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                      <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    ICT Management
                  </h3>

                  {/* ICT Status Toggle */}
                  <div className="mb-4">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.with_ict}
                        onChange={(e) => {
                          const newWithICT = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            with_ict: newWithICT,
                            details: newWithICT ? {
                              has_charger: false,
                              has_bag: false,
                              stack_number: undefined,
                              position_in_stack: undefined,
                              recall_date: new Date().toISOString().split('T')[0],
                              return_due_date: undefined,
                              condition_notes: ''
                            } : null
                          }));
                        }}
                        disabled={isReadOnly}
                        className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                      />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Device is with ICT
                      </span>
                    </label>
                  </div>

                  {/* ICT Details - Only show when with_ict is true */}
                  {formData.with_ict && formData.details && (
                    <div className="space-y-4">
                      {/* Accessories */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Accessories
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.details.has_charger}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                details: prev.details ? { ...prev.details, has_charger: e.target.checked } : null
                              }))}
                              disabled={isReadOnly}
                              className="w-4 h-4 text-green-600 bg-white border-slate-300 rounded focus:ring-green-500"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">Has Charger</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.details.has_bag}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                details: prev.details ? { ...prev.details, has_bag: e.target.checked } : null
                              }))}
                              disabled={isReadOnly}
                              className="w-4 h-4 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-300">Has Bag</span>
                          </label>
                        </div>
                      </div>

                      {/* Storage Location */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Stack Number
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={formData.details.stack_number || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              details: prev.details ? { 
                                ...prev.details, 
                                stack_number: e.target.value ? parseInt(e.target.value) : undefined 
                              } : null
                            }))}
                            disabled={isReadOnly}
                            placeholder="1-99"
                            className={`w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                              isReadOnly 
                                ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                                : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                            }`}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Position in Stack
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            value={formData.details.position_in_stack || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              details: prev.details ? { 
                                ...prev.details, 
                                position_in_stack: e.target.value ? parseInt(e.target.value) : undefined 
                              } : null
                            }))}
                            disabled={isReadOnly}
                            placeholder="1-20"
                            className={`w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                              isReadOnly 
                                ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                                : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Condition Notes */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Condition Notes
                        </label>
                        <textarea
                          value={formData.details.condition_notes || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            details: prev.details ? { ...prev.details, condition_notes: e.target.value } : null
                          }))}
                          disabled={isReadOnly}
                          rows={2}
                          placeholder="Note any damage, issues, or special conditions..."
                          className={`w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-y ${
                            isReadOnly 
                              ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                              : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          }`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                {/* Unlock Code */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Unlock Code
                  </label>
                  <div className="relative">
                    <input
                      type={showUnlockCode ? "text" : "password"}
                      name="unlock_code"
                      value={formData.unlock_code}
                      onChange={handleChange}
                      readOnly={isReadOnly}
                      className={`w-full px-4 py-3 pr-20 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-mono text-sm ${
                        isReadOnly 
                          ? 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400' 
                          : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      }`}
                      placeholder="12-character code"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => setShowUnlockCode(!showUnlockCode)}
                        className="p-1 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                        title={showUnlockCode ? "Hide code" : "Show code"}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => {
                            const oldCode = formData.unlock_code;
                            const newCode = generateUnlockCode();
                            setFormData(prev => ({ ...prev, unlock_code: newCode }));
                            
                            // Log the unlock code regeneration
                            if (device) {
                              logAction(
                                'regenerate_unlock_code',
                                'security',
                                {
                                  resourceType: 'device',
                                  resourceId: device.id,
                                  details: {
                                    device_name: formData.device_name,
                                    serial_number: formData.serial_number,
                                    old_code_length: oldCode.length,
                                    new_code_length: newCode.length
                                  },
                                  success: true
                                }
                              );
                            }
                          }}
                          className="p-1 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Generate new code"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(formData.unlock_code)}
                        className="p-1 text-slate-500 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    For manual unlock if remote fails
                  </p>
                </div>

                {/* Lock Status */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    Device Status
                  </label>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => !isReadOnly && setFormData(prev => ({ ...prev, is_locked: false, lock_message_id: null }))}
                      disabled={isReadOnly}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                        !formData.is_locked
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                          : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:border-emerald-300 dark:hover:border-emerald-600'
                      } ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:shadow-md'}`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        !formData.is_locked
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}>
                        {!formData.is_locked && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                      </div>
                      <Unlock className="w-4 h-4" />
                      <span className="font-medium text-sm">Unlocked</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => !isReadOnly && setFormData(prev => ({
                        ...prev,
                        is_locked: true,
                        lock_message_id: prev.lock_message_id ?? defaultLockMessage?.id ?? null
                      }))}
                      disabled={isReadOnly}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                        formData.is_locked
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                          : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:border-red-300 dark:hover:border-red-600'
                      } ${isReadOnly ? 'cursor-default' : 'cursor-pointer hover:shadow-md'}`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.is_locked
                          ? 'border-red-500 bg-red-500'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}>
                        {formData.is_locked && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                      </div>
                      <Lock className="w-4 h-4" />
                      <span className="font-medium text-sm">Locked</span>
                    </button>
                  </div>

                  <div className="mt-3 space-y-2">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400">
                      Lock Message Template
                    </label>

                    {formData.is_locked ? (
                      <>
                        {isReadOnly ? (
                          <div className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm text-slate-700 dark:text-slate-300">
                            {selectedLockMessage
                              ? selectedLockMessage.title
                              : 'No template assigned (legacy lock)'}
                          </div>
                        ) : lockMessagesLoading ? (
                          <div className="text-xs text-slate-500 dark:text-slate-400">Loading templates...</div>
                        ) : lockMessages.length === 0 ? (
                          <div className="text-xs text-amber-600 dark:text-amber-400">
                            No templates available. Create one in Lock Messages before saving a locked device.
                          </div>
                        ) : (
                          <select
                            value={formData.lock_message_id ?? ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              lock_message_id: e.target.value ? Number(e.target.value) : null
                            }))}
                            className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                          >
                            <option value="">Select template...</option>
                            {lockMessages.map((message) => (
                              <option key={message.id} value={message.id}>
                                {message.title}{message.is_default ? ' (Default)' : ''}
                              </option>
                            ))}
                          </select>
                        )}

                        {!isReadOnly && selectedLockMessage && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Assigned template: {selectedLockMessage.title}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Template assignment is only required when device status is Locked.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {!isReadOnly && (
              <div className="flex justify-end space-x-3 pt-5 border-t border-slate-200/60 dark:border-slate-700/60">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Saving...' : 'Save Device'}</span>
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}